import React, { Fragment, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import styled from '@emotion/styled';

import StockBlock from '../common/StockBlock';

import InputWrapper from '../../common/InputWrapper';
import Select from '../../common/Select';
import Button from '../../common/Button';
import Checkbox from '../../common/Checkbox';

import { checkIfProductHasVariants, getEshopType } from '../../../helpers';
import CategoriesListBlock from './../common/CategoriesListBlock';
import { useAutoTranslation } from '../../../helpers/useAutoTranslation';

const Wrapper = styled('div')`
  padding: 1.5rem 0;
  > div:not(.border-block) {
    margin-left: calc(1.5rem - 12px);
    margin-right: calc(1.5rem - 12px);
  }
  .border-block {
    border-top: 1px solid #f7f6f6;
  }
  .product-id-top {
    position: relative;
  }
  .product-id {
    cursor: pointer;
    border-bottom: 1px dashed #72747b;
  }
  .help-tooltip {
    min-width: 0;
    right: 0;
    left: unset;
    cursor: text;
    white-space: nowrap;
  }

  .add-barcode-dropdown-shadow {
    position: fixed;
    opacity: 0.2;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    z-index: 1;
  }

  .form-group {
    position: relative;
  }

  .options-list {
    position: absolute;
    top: 27px;
    right: 0;
    background: white;
    z-index: 1;
    border: 1px solid #ced4da;
    border-radius: 4px;
    padding: 5px;
    list-style: none;
    width: 180px;

    .options-item {
      padding: 9px 19px;
      cursor: pointer;

      &:hover {
        background: #7e57c310;
      }
    }
  }

  .barcode-type-info {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 10px;
    margin: auto;
    height: fit-content;
    color: #7e57c2;
    pointer-events: none;
  }

  .barcode-type-info-first {
    top: 27px;
  }

  .weight-group {
    .react-select__control {
      width: 110px;
      height: 36px;
    }
  }

  .warehouse-dimensions {
    background: #f1f5f7;
    border-radius: 4px;
    padding: 6px 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
  }
  .warehouse-dimensions-text {
    font-weight: 500;
    font-size: 12px;
    line-height: 20px;
    color: #949cb5;
    margin-right: 20px;
    span {
      font-weight: normal;
    }
  }
  .btn-copy {
    display: flex;
    align-items: center;
    padding: 0;
    font-size: 12px;
    line-height: 20px;
    i {
      margin-right: 4px;
    }
  }

  .non-active-input {
    background-color: #f3f3f3;
  }

  @media (max-width: 1360px) and (min-width: 992px) {
    .warehouse-dimensions {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  @media (max-width: 530px) {
    .warehouse-dimensions {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  @media (max-width: 380px) {
    .warehouse-dimensions-text span {
      display: block;
    }
  }

  .parameters-checkboxes {
    display: flex;
  }

  @media (max-width: 530px) {
    .parameters-checkboxes {
      flex-direction: column;
      .custom-checkbox:not(:first-child) {
        margin-top: 10px;
      }
    }
  }
`;

const weightTypes = [
  { value: 'kg', label: 'кг' },
  { value: 'gr', label: 'гр' },
];

const ParametersBlock = (props) => {
  const {
    isNew,
    currentProduct,
    values,
    warehouses,
    errors,
    handleChangeInput,
    handleChangeWeightType,
    handleSubmit,
    type,
    eshops,
  } = props;

  const [dropdownVisible, setDropdownVisible] = useState(false);

  const adminMode = localStorage.getItem('adminPanel');

  const { t } = useAutoTranslation();

  const handleAddOneMoreBarcode = (type) => {
    const newBarcodes = JSON.parse(JSON.stringify(values.barcodes));
    newBarcodes.push({
      value: '',
      isDefault: false,
      type,
    });
    handleChangeInput('barcodes', newBarcodes);
    setDropdownVisible(false);
  };

  const handleChangeBarcodes = (e, index, reset = false) => {
    const newBarcodes = JSON.parse(JSON.stringify(values.barcodes));
    reset === false ? (newBarcodes[index].value = e.target.value) : (newBarcodes[index].value = e);
    handleChangeInput('barcodes', newBarcodes);
  };

  const handleRemoveBarcode = (index) => {
    const newBarcodes = JSON.parse(JSON.stringify(values.barcodes));
    newBarcodes.splice(index, 1);
    handleChangeInput('barcodes', newBarcodes);
  };

  const handleActivityStatusBarcode = (value, index) => {
    const newBarcodes = JSON.parse(JSON.stringify(values.barcodes));
    newBarcodes[index].disabled = value;
    handleChangeInput('barcodes', newBarcodes);
  };

  let productIdTop = null;
  if (!isNew && currentProduct) {
    productIdTop = `${t('ID продукта')}: ${currentProduct.id}`;
    if (currentProduct.extId) {
      productIdTop += ` (${currentProduct.extId})`;
    }
  }

  const [showPopup, setShowPopup] = useState(false);
  const [showExtIds, setShowExtIds] = useState(false);

  const [eshopSettings, setEshopSettings] = useState({});

  const baseVariant = currentProduct?.variants?.[0];

  const shouldShowWarehouseDimensionsAndWeight =
    baseVariant?.weightFact &&
    baseVariant?.dimensionsFact?.width &&
    baseVariant?.dimensionsFact?.height &&
    baseVariant?.dimensionsFact?.depth;

  useEffect(() => {
    if (values.eshopSettings?.length) {
      let newEshopSettings = {};
      values.eshopSettings.forEach((shop) => {
        newEshopSettings[shop.eshopId] = {
          ...shop,
        };
      });
      setEshopSettings(newEshopSettings);
    } else {
      setEshopSettings({});
    }
  }, [values.eshopSettings]);

  const checkIfShopExist = (id) => {
    return values.eshopSettings.find((shop) => shop.eshopId === id);
  };

  const removeShop = (id) => {
    return [...values.eshopSettings].filter((shop) => shop.eshopId !== id);
  };

  const handleChangeShopSettings = (type, id, value) => {
    let newEshopSettings = [];
    if (!value) {
      if (type === 'extId') {
        if (values.eshopSettings?.length) {
          const isExist = checkIfShopExist(id);
          if (isExist) {
            newEshopSettings = removeShop(id);
            handleChangeInput('eshopSettings', newEshopSettings);
          }
          return;
        }
      }
      if (type === 'enabled') {
        if (values.eshopSettings?.length) {
          const isExist = checkIfShopExist(id);
          if (isExist) {
            if (!isExist.extId) {
              newEshopSettings = removeShop(id);
              handleChangeInput('eshopSettings', newEshopSettings);
              return;
            }
          }
        }
      }
    }
    if (values.eshopSettings?.length) {
      if (type === 'enabled' && !value) {
        toast.info('Внимание! Скорректируйте остаток этого товара на маркетплейсе вручную');
      }
      const isExist = checkIfShopExist(id);
      if (isExist) {
        newEshopSettings = [...values.eshopSettings].map((shop) => {
          if (shop.eshopId === id) {
            return {
              ...shop,
              [type]: value,
            };
          } else {
            return {
              ...shop,
            };
          }
        });
      } else {
        newEshopSettings = [
          ...values.eshopSettings,
          {
            eshopId: id,
            enabled: false,
            extId: '',
            [type]: value,
          },
        ];
      }
    } else {
      newEshopSettings = [
        {
          eshopId: id,
          enabled: false,
          extId: '',
          [type]: value,
        },
      ];
    }
    handleChangeInput('eshopSettings', newEshopSettings);
  };

  const handleCopyWeightAndDimensions = () => {
    if (baseVariant?.weightFact) {
      if (values.weightType !== 'gr') {
        handleChangeWeightType('gr');
      }
      handleChangeInput('weight', baseVariant.weightFact);
    }
    if (baseVariant?.dimensionsFact) {
      handleChangeInput('width', baseVariant.dimensionsFact?.width ?? 0);
      handleChangeInput('height', baseVariant.dimensionsFact?.height ?? 0);
      handleChangeInput('depth', baseVariant.dimensionsFact?.depth ?? 0);
    }
    if (values.autoDimensionsCalculation) {
      handleChangeInput('autoDimensionsCalculation', false);
    }
  };

  return (
    <div className="col-lg-6">
      <Wrapper className="card-box">
        {values.type !== 'AD_ATTACHMENT' ? (
          <div className="row d-flex justify-content-between">
            <div className="col-md-4">
              <h4 className="header-title">{t('Параметры')}</h4>
            </div>
            {type === 'BUNDLE' && (
              <div className="col-md-4 d-flex justify-content-md-end mb-md-0 mb-2">
                <Checkbox
                  id="autoDimensionsCalculation"
                  label={t('Авторасчет веса и габаритов')}
                  labelQuestion={t('Авторасчет веса и габаритов')}
                  checked={values.autoDimensionsCalculation}
                  onChange={(e) => handleChangeInput('autoDimensionsCalculation', e.target.checked)}
                />
              </div>
            )}
          </div>
        ) : null}

        {values.type !== 'SUBSCRIPTION' && values.type !== 'AD_ATTACHMENT' && (
          <>
            <div className="row">
              <div className="col-12">
                <div className="form-group weight-group">
                  <InputWrapper
                    type="text"
                    label={t('Вес')}
                    id="weight"
                    value={values.weight}
                    error={errors.weight}
                    onChange={(e) => handleChangeInput('weight', e.target.value)}
                    textAppend={
                      <Select
                        options={weightTypes}
                        value={weightTypes.find((t) => t.value === values.weightType) || undefined}
                        onChange={(e) => handleChangeWeightType(e.value)}
                        isDisabled={values.autoDimensionsCalculation}
                      />
                    }
                    placeholder={t('Введите вес')}
                    readOnly={values.autoDimensionsCalculation}
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-4 col-md-4 col-lg-12 col-xl-4">
                <div className="form-group">
                  <InputWrapper
                    type="number"
                    label={t('Ширина')}
                    id="width"
                    value={values.width}
                    error={errors.width}
                    onChange={(e) => handleChangeInput('width', e.target.value)}
                    textAppend={t('см')}
                    showValidation
                    readOnly={values.autoDimensionsCalculation}
                  />
                </div>
              </div>
              <div className="col-sm-4 col-md-4 col-lg-12 col-xl-4">
                <div className="form-group">
                  <InputWrapper
                    type="number"
                    label={t('Высота')}
                    id="height"
                    value={values.height}
                    error={errors.height}
                    onChange={(e) => handleChangeInput('height', e.target.value)}
                    textAppend={t('см')}
                    showValidation
                    readOnly={values.autoDimensionsCalculation}
                  />
                </div>
              </div>
              <div className="col-sm-4 col-md-4 col-lg-12 col-xl-4">
                <div className="form-group">
                  <InputWrapper
                    type="number"
                    label={t('Глубина')}
                    id="depth"
                    value={values.depth}
                    error={errors.depth}
                    onChange={(e) => handleChangeInput('depth', e.target.value)}
                    textAppend={t('см')}
                    showValidation
                    readOnly={values.autoDimensionsCalculation}
                  />
                </div>
              </div>
            </div>
            <>
              {shouldShowWarehouseDimensionsAndWeight ? (
                <div className="row">
                  <div className="col-12">
                    <div className="warehouse-dimensions">
                      <span className="warehouse-dimensions-text">
                        {t('Текущие габариты и вес')}:{' '}
                        <span>
                          {' '}
                          {baseVariant?.dimensionsFact
                            ? `${baseVariant.dimensionsFact.width ?? 0} x ${
                                baseVariant.dimensionsFact.height ?? 0
                              } x ${baseVariant.dimensionsFact.depth ?? 0} см`
                            : null}{' '}
                          {baseVariant?.dimensionsFact && baseVariant?.weightFact ? ` | ` : null}{' '}
                          {baseVariant?.weightFact ? `${baseVariant.weightFact} гр` : null}{' '}
                        </span>
                      </span>
                      <Button
                        classType="btn-link btn-copy"
                        type="button"
                        text={
                          <span>
                            <i className="fe-copy" style={{ color: '#7e57c2', fontSize: '14px' }} />
                            {t('Скопировать и подставить')}
                          </span>
                        }
                        onClick={handleCopyWeightAndDimensions}
                      />
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="row">
                <div className="col-12">
                  <div className="form-group">
                    <InputWrapper
                      type="text"
                      label={t('Артикул')}
                      id="article"
                      value={values.article}
                      error={errors.article}
                      onChange={(e) => handleChangeInput('article', e.target.value)}
                      placeholder={t('Введите артикул')}
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-10">
                  <div className="form-group">
                    <InputWrapper
                      type="text"
                      label={t('Штрихкод')}
                      id="barcode"
                      value={values?.barcodes[0].value}
                      error={errors.barcodes}
                      onChange={(e) => handleChangeBarcodes(e, 0)}
                      placeholder={t('Введите штрихкод')}
                      addButton
                      addButtonText={t('Еще штрихкод')}
                      onAddButtonClick={() => setDropdownVisible(true)}
                      inputClassName={
                        values?.barcodes[0].disabled ? 'non-active-input form-control' : false
                      }
                    />
                    {values.barcodes[0].type === `COMMON` && (
                      <span className="barcode-type-info barcode-type-info-first">
                        {t('Обычный')}
                      </span>
                    )}
                    {values.barcodes[0].type === `WILDBERRIES` && (
                      <span className="barcode-type-info barcode-type-info-first">Wildberries</span>
                    )}
                    {values.barcodes[0].type === `OZON` && (
                      <span className="barcode-type-info barcode-type-info-first">OZON</span>
                    )}
                    {values.barcodes[0].type === `SBERMEGAMARKET` && (
                      <span className="barcode-type-info barcode-type-info-first">
                        СберМегаМаркет
                      </span>
                    )}
                    {values.barcodes[0].type === `LETOILE` && (
                      <span className="barcode-type-info barcode-type-info-first">Лэтуаль</span>
                    )}
                    {values.barcodes[0].type === `ZOLOTOE_YABLOKO` && (
                      <span className="barcode-type-info barcode-type-info-first">
                        Золотое Яблоко
                      </span>
                    )}
                    {values.barcodes[0].type === `YANDEX_MARKET` && (
                      <span className="barcode-type-info barcode-type-info-first">
                        Яндекс Маркет
                      </span>
                    )}
                    {values.barcodes[0].type === `LAMODA` && (
                      <span className="barcode-type-info barcode-type-info-first">Lamoda</span>
                    )}
                    {values.barcodes[0].type === `DET_MIR` && (
                      <span className="barcode-type-info barcode-type-info-first">Детский мир</span>
                    )}
                    {values.barcodes[0].type === `VSEINSTRUMENTI` && (
                      <span className="barcode-type-info barcode-type-info-first">
                        Все инструменты
                      </span>
                    )}
                    {values.barcodes[0].type === `RETAIL_NETWORK_4` && (
                      <span className="barcode-type-info barcode-type-info-first">
                        {t('Розничные сети групповой ШК')} 4
                      </span>
                    )}
                    {values.barcodes[0].type === `RETAIL_NETWORK_6` && (
                      <span className="barcode-type-info barcode-type-info-first">
                        {t('Розничные сети групповой ШК')} 6
                      </span>
                    )}
                    {values.barcodes[0].type === `RETAIL_NETWORK_24` && (
                      <span className="barcode-type-info barcode-type-info-first">
                        {t('Розничные сети групповой ШК')} 24
                      </span>
                    )}

                    {dropdownVisible && (
                      <>
                        <span
                          className="add-barcode-dropdown-shadow"
                          onClick={() => setDropdownVisible(false)}
                        />
                        <ul className="options-list">
                          <li
                            className="options-item"
                            onClick={() => handleAddOneMoreBarcode(`COMMON`)}
                          >
                            {t('Обычный')}
                          </li>
                          <li
                            className="options-item"
                            onClick={() => handleAddOneMoreBarcode(`WILDBERRIES`)}
                          >
                            Wildberries
                          </li>
                          <li
                            className="options-item"
                            onClick={() => handleAddOneMoreBarcode(`OZON`)}
                          >
                            OZON
                          </li>
                          <li
                            className="options-item"
                            onClick={() => handleAddOneMoreBarcode(`SBERMEGAMARKET`)}
                          >
                            СберМегаМаркет
                          </li>
                          <li
                            className="options-item"
                            onClick={() => handleAddOneMoreBarcode(`LETOILE`)}
                          >
                            Лэтуаль
                          </li>
                          <li
                            className="options-item"
                            onClick={() => handleAddOneMoreBarcode(`ZOLOTOE_YABLOKO`)}
                          >
                            Золотое Яблоко
                          </li>
                          <li
                            className="options-item"
                            onClick={() => handleAddOneMoreBarcode(`YANDEX_MARKET`)}
                          >
                            Яндекс Маркет
                          </li>
                          <li
                            className="options-item"
                            onClick={() => handleAddOneMoreBarcode(`LAMODA`)}
                          >
                            Lamoda
                          </li>
                          <li
                            className="options-item"
                            onClick={() => handleAddOneMoreBarcode(`DET_MIR`)}
                          >
                            Детский мир
                          </li>
                          <li
                            className="options-item"
                            onClick={() => handleAddOneMoreBarcode(`VSEINSTRUMENTI`)}
                          >
                            Все инструменты
                          </li>
                          <li
                            className="options-item"
                            onClick={() => handleAddOneMoreBarcode(`RETAIL_NETWORK`)} //ZDRAV_CITY
                          >
                            Здравсити
                          </li>
                          <li
                            className="options-item"
                            onClick={() => handleAddOneMoreBarcode(`RETAIL_NETWORK_4`)}
                          >
                            {t('Розничные сети групповой ШК')} 4
                          </li>
                          <li
                            className="options-item"
                            onClick={() => handleAddOneMoreBarcode(`RETAIL_NETWORK_6`)}
                          >
                            {t('Розничные сети групповой ШК')} 6
                          </li>
                          <li
                            className="options-item"
                            onClick={() => handleAddOneMoreBarcode(`RETAIL_NETWORK_24`)}
                          >
                            {t('Розничные сети групповой ШК')} 24
                          </li>
                        </ul>
                      </>
                    )}
                  </div>
                </div>
                <div
                  className="col-2 removing-block"
                  style={{
                    display: 'flex',
                    padding: '0',
                    alignItems: 'center',
                    maxWidth: '60px',
                    marginTop: '10px',
                  }}
                >
                  {adminMode === 'true' ? (
                    <span className="help-tooltip-wrapper">
                      <Checkbox
                        id={values.barcodes[0].value}
                        inputClass="admin-checkbox-bg-color fe-help-circle"
                        checked={values.barcodes[0].disabled}
                        onChange={(e) => handleActivityStatusBarcode(e.target.checked, 0)}
                      />
                      <span className="help-tooltip">
                        {values.barcodes[0].disabled ? t('Неактивен') : t('Активен')}
                      </span>
                    </span>
                  ) : null}
                  <Button
                    style={{
                      marginLeft: 0,
                      paddingLeft: '13px',
                      marginLeft: 'auto',
                      paddingRight: 0,
                    }}
                    classType="btn-link"
                    type="button"
                    text={
                      <i className="fe-trash-2" style={{ color: '#6c757d', fontSize: '20px' }} />
                    }
                    onClick={
                      values.barcodes.length === 1
                        ? () => handleChangeBarcodes('', 0, true)
                        : () => handleRemoveBarcode(0)
                    }
                  />
                </div>
              </div>
              {values.barcodes.length > 0 ? (
                <Fragment>
                  {values.barcodes.map((b, i) => {
                    return (
                      <Fragment key={`barcode-${i}`}>
                        {i !== 0 ? (
                          <div className="row">
                            <div className="col-10">
                              <div className="form-group">
                                <InputWrapper
                                  type="text"
                                  noLabel
                                  id="barcode"
                                  value={values?.barcodes[i].value}
                                  error={errors.barcodes}
                                  onChange={(e) => handleChangeBarcodes(e, i)}
                                  placeholder={t('Введите штрихкод')}
                                  inputClassName={
                                    values?.barcodes[i].disabled
                                      ? 'non-active-input form-control'
                                      : false
                                  }
                                />
                                {values.barcodes[i].type === `COMMON` && (
                                  <span className="barcode-type-info">{t('Обычный')}</span>
                                )}
                                {values.barcodes[i].type === `WILDBERRIES` && (
                                  <span className="barcode-type-info">Wildberries</span>
                                )}
                                {values.barcodes[i].type === `OZON` && (
                                  <span className="barcode-type-info">OZON</span>
                                )}
                                {values.barcodes[i].type === `SBERMEGAMARKET` && (
                                  <span className="barcode-type-info">СберМегаМаркет</span>
                                )}
                                {values.barcodes[i].type === `LETOILE` && (
                                  <span className="barcode-type-info">Лэтуаль</span>
                                )}
                                {values.barcodes[i].type === `ZOLOTOE_YABLOKO` && (
                                  <span className="barcode-type-info">Золотое Яблоко</span>
                                )}
                                {values.barcodes[i].type === `YANDEX_MARKET` && (
                                  <span className="barcode-type-info">Яндекс Маркет</span>
                                )}
                                {values.barcodes[i].type === `LAMODA` && (
                                  <span className="barcode-type-info">Lamoda</span>
                                )}
                                {values.barcodes[i].type === `DET_MIR` && (
                                  <span className="barcode-type-info">Детский мир</span>
                                )}
                                {values.barcodes[i].type === `VSEINSTRUMENTI` && (
                                  <span className="barcode-type-info">Все инструменты</span>
                                )}
                                {values.barcodes[i].type === `RETAIL_NETWORK_4` && (
                                  <span className="barcode-type-info">
                                    {t('Розничные сети групповой ШК')} 4
                                  </span>
                                )}
                                {values.barcodes[i].type === `RETAIL_NETWORK_6` && (
                                  <span className="barcode-type-info">
                                    {t('Розничные сети групповой ШК')} 6
                                  </span>
                                )}
                                {values.barcodes[i].type === `RETAIL_NETWORK_24` && (
                                  <span className="barcode-type-info">
                                    {t('Розничные сети групповой ШК')} 24
                                  </span>
                                )}
                              </div>
                            </div>
                            <div
                              className="col-2 removing-block"
                              style={{
                                display: 'flex',
                                padding: '0',
                                marginTop: '-15px',
                                alignItems: 'center',
                                maxWidth: '60px',
                              }}
                            >
                              {adminMode === 'true' ? (
                                <span className="help-tooltip-wrapper">
                                  <Checkbox
                                    id={`${values.barcodes[i].value}_${values.barcodes[i].type}`}
                                    inputClass="admin-checkbox-bg-color fe-help-circle"
                                    checked={values.barcodes[i].disabled}
                                    onChange={(e) =>
                                      handleActivityStatusBarcode(e.target.checked, i)
                                    }
                                  />
                                  <span className="help-tooltip">
                                    {values.barcodes[i].disabled ? t('Неактивен') : t('Активен')}
                                  </span>
                                </span>
                              ) : null}
                              <Button
                                style={{
                                  marginLeft: 0,
                                  paddingLeft: '13px',
                                  marginLeft: 'auto',
                                  paddingRight: 0,
                                }}
                                classType="btn-link"
                                type="button"
                                text={
                                  <i
                                    className="fe-trash-2"
                                    style={{ color: '#6c757d', fontSize: '20px' }}
                                  />
                                }
                                onClick={() => handleRemoveBarcode(i)}
                              />
                            </div>
                          </div>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </Fragment>
              ) : null}

              {values.stock && values.stock.length ? (
                <StockBlock
                  stock={values.stock}
                  variant={
                    currentProduct &&
                    currentProduct.variants &&
                    currentProduct.variants.length &&
                    currentProduct.variants[0]
                  }
                  warehouses={warehouses}
                  handleChangeInput={handleChangeInput}
                  handleSubmit={handleSubmit}
                />
              ) : null}
            </>

            <div className="border-block" style={{ marginBottom: '18px' }} />
          </>
        )}

        {values.type === 'AD_ATTACHMENT' ? (
          <>
            <div className="row">
              <div className="col-12">
                <div className="form-group">
                  <InputWrapper
                    type="text"
                    label={t('Штрихкод')}
                    id="barcode"
                    value={values.barcodes[0].value}
                    error={errors.barcodes}
                    onChange={(e) => handleChangeBarcodes(e, 0)}
                    placeholder={t('Введите штрихкод')}
                    addButton
                    addButtonText={t('Еще штрихкод')}
                    onAddButtonClick={() => setDropdownVisible(true)}
                  />
                  {values.barcodes[0].type === `WILDBERRIES` && (
                    <span className="barcode-type-info barcode-type-info-first">Wildberries</span>
                  )}
                  {dropdownVisible && (
                    <>
                      <span
                        className="add-barcode-dropdown-shadow"
                        onClick={() => setDropdownVisible(false)}
                      />
                      <ul className="options-list">
                        <li
                          className="options-item"
                          onClick={() => handleAddOneMoreBarcode(`COMMON`)}
                        >
                          {t('Обычный')}
                        </li>
                        <li
                          className="options-item"
                          onClick={() => handleAddOneMoreBarcode(`WILDBERRIES`)}
                        >
                          Wildberries
                        </li>
                      </ul>
                    </>
                  )}
                </div>
              </div>
            </div>
            {values.barcodes.length > 1 ? (
              <Fragment>
                {values.barcodes.map((b, i) => (
                  <Fragment key={`barcode-${i}`}>
                    {i !== 0 ? (
                      <div className="row">
                        <div className="col-10">
                          <div className="form-group">
                            <InputWrapper
                              type="text"
                              noLabel
                              id="barcode"
                              value={values.barcodes[i].value}
                              error={errors.barcodes}
                              onChange={(e) => handleChangeBarcodes(e, i)}
                              placeholder={t('Введите штрихкод')}
                            />
                            {values.barcodes[i].type === `WILDBERRIES` && (
                              <span className="barcode-type-info">Wildberries</span>
                            )}
                          </div>
                        </div>
                        <div
                          className="col-1 removing-block"
                          style={{ display: 'flex', paddingRight: '0', marginTop: '-15px' }}
                        >
                          <Button
                            style={{ marginLeft: 0, paddingLeft: 0 }}
                            classType="btn-link"
                            type="button"
                            text={
                              <i
                                className="fe-trash-2"
                                style={{ color: '#6c757d', fontSize: '20px' }}
                              />
                            }
                            onClick={() => handleRemoveBarcode(i)}
                          />
                        </div>
                      </div>
                    ) : null}
                  </Fragment>
                ))}
              </Fragment>
            ) : null}
          </>
        ) : null}

        {currentProduct && currentProduct.variants && currentProduct.variants.length ? (
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <div style={{ display: 'flex' }}>
                  <label htmlFor="shortName" style={{ marginRight: 5 }}>
                    ID
                  </label>
                  {productIdTop ? (
                    <div className="product-id-top">
                      <div className="help-tooltip-wrapper">
                        <div className="help-tooltip" style={{ display: showPopup && `block` }}>
                          {productIdTop}
                        </div>
                      </div>
                      <p className="product-id" onClick={() => setShowPopup(!showPopup)}>
                        {t('ID продукта')}
                      </p>
                    </div>
                  ) : null}
                </div>
                <InputWrapper
                  noLabel
                  type="text"
                  label="ID"
                  id="variantId"
                  value={currentProduct.variants[0].id}
                  readOnly
                />
              </div>
            </div>
          </div>
        ) : null}

        {values.type !== 'AD_ATTACHMENT' && eshops && eshops.length ? (
          <div className="row">
            <div className="col-12">
              {eshops.map((shop) => (
                <div className="form-group" key={shop.id}>
                  <div className="d-flex justify-content-between flex-md-row flex-column">
                    <label className="mb-md-0 mb-2" htmlFor={shop.id}>
                      {t('Внешний ID для')} {shop.name ? `"${shop.name}"` : null}{' '}
                      {shop.type ? `(${getEshopType(shop.type)})` : null}
                    </label>
                    <Checkbox
                      label={t('Обнулить остаток')}
                      id={`checkbox-zeroStock-${shop.id}`}
                      checked={eshopSettings[shop.id] ? eshopSettings[shop.id].zeroStock : false}
                      onChange={(e) =>
                        handleChangeShopSettings('zeroStock', shop.id, e.target.checked)
                      }
                      wrapperStyle={{ margin: '0 20px 0 auto' }}
                    />
                    <Checkbox
                      label={t('Разместить на площадке')}
                      id={`checkbox-enabled-${shop.id}`}
                      checked={eshopSettings[shop.id] ? eshopSettings[shop.id].enabled : false}
                      onChange={(e) =>
                        handleChangeShopSettings('enabled', shop.id, e.target.checked)
                      }
                    />
                  </div>
                  <InputWrapper
                    type="text"
                    id={shop.id}
                    value={eshopSettings[shop.id]?.extId || ''}
                    onChange={(e) => handleChangeShopSettings('extId', shop.id, e.target.value)}
                    placeholder={`${t('Введите внешний ID для')} ${shop.name}`}
                  />

                  <div className="row">
                    <div className="id-input-wrapper col-sm-4 col-md-4 col-lg-12 col-xl-4">
                      <label className="mb-md-0 mb-2" htmlFor={shop.id}>
                        {t('Процент остатков')}
                      </label>
                      <div className="percent-stock-wrapper">
                        <InputWrapper
                          type="text"
                          id={shop.id}
                          value={eshopSettings[shop.id]?.percentStock || ''}
                          onChange={(e) =>
                            handleChangeShopSettings('percentStock', shop.id, e.target.value)
                          }
                        />
                      </div>
                    </div>
                    <div className="id-input-wrapper ">
                      <label className="mb-md-0 mb-2" htmlFor={shop.id}>
                        {t('Выгружать последние N шт в данный магазин')}
                      </label>
                      <div className="percent-stock-wrapper col-sm-4 col-md-4 col-lg-12 col-xl-4 p-0">
                        <InputWrapper
                          type="text"
                          id={shop.id}
                          value={eshopSettings[shop.id]?.lastStock || ''}
                          onChange={(e) =>
                            handleChangeShopSettings('lastStock', shop.id, e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="row">
          <div className="col-12">
            <div className="form-group">
              <InputWrapper
                type="text"
                label={t('Внешний ID')}
                id="extId"
                value={values.extId}
                error={errors.extId}
                onChange={(e) => handleChangeInput('extId', e.target.value)}
                placeholder={t('Введите внешний ID')}
              />
            </div>
            <div className="form-group">
              {currentProduct && !checkIfProductHasVariants(currentProduct) ? (
                <InputWrapper
                  type="text"
                  label={`${t('Внешний ID')} 2`}
                  id="extId2"
                  value={values.extId2}
                  error={errors.extId2}
                  onChange={(e) => handleChangeInput('extId2', e.target.value)}
                  placeholder={`${t('Введите внешний ID')} 2`}
                />
              ) : null}
            </div>
            {showExtIds ? (
              <>
                <div className="form-group">
                  {currentProduct && !checkIfProductHasVariants(currentProduct) ? (
                    <InputWrapper
                      type="text"
                      label={`${t('Внешний ID')} 3`}
                      id="extId2"
                      value={values.extId3}
                      error={errors.extId3}
                      onChange={(e) => handleChangeInput('extId3', e.target.value)}
                      placeholder={`${t('Введите внешний ID')} 3`}
                    />
                  ) : null}
                </div>
                <div className="form-group">
                  {currentProduct && !checkIfProductHasVariants(currentProduct) ? (
                    <InputWrapper
                      type="text"
                      label={`${t('Внешний ID')} 4`}
                      id="extId4"
                      value={values.extId4}
                      error={errors.extId4}
                      onChange={(e) => handleChangeInput('extId4', e.target.value)}
                      placeholder={`${t('Введите внешний ID')} 4`}
                    />
                  ) : null}
                </div>
                <div className="form-group">
                  {currentProduct && !checkIfProductHasVariants(currentProduct) ? (
                    <InputWrapper
                      type="text"
                      label={`${t('Внешний ID')} 5`}
                      id="extId5"
                      value={values.extId5}
                      error={errors.extId5}
                      onChange={(e) => handleChangeInput('extId5', e.target.value)}
                      placeholder={`${t('Введите внешний ID')} 5`}
                    />
                  ) : null}
                </div>
              </>
            ) : (
              <div
                style={{ color: 'rgb(126, 87, 194)', cursor: 'pointer' }}
                onClick={() => setShowExtIds(true)}
              >
                {t('Все внешние ID')}
              </div>
            )}
          </div>
        </div>
      </Wrapper>
    </div>
  );
};

export default ParametersBlock;
